import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { API, ClientDTO, UserFullDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { UserService } from 'src/app/services/user.service';
import { UtilService } from 'src/app/services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { AdminClientAdminManagementModalComponent } from './admin-client-admin-management-modal/admin-client-admin-management-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { RequestClass } from '../../../services/global/RequestClass';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { RoutingID } from '../../../routes/sb-routing-enum';

type SplitButtonAction = 'admins';

@Component({
  selector: 'sb-page-admin-client-management',
  templateUrl: './admin-client-management.component.html',
  styleUrls: ['./admin-client-management.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminClientManagementComponent extends RequestClass {
  public readonly userStatus = inject(UserService);
  public readonly modal = inject(ModalService);
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly translate = inject(TranslateService);
  private readonly api = inject(API);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  // table wrapper variables
  clients: ClientDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      ._admin_Client_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (clients: ClientDTO[]) => {
          this.clients = this.buildSplitButtons(clients);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  manageAdmins(pClient: ClientDTO) {
    this.modal
      .showNavigationModal({
        entryComponent: { value: AdminClientAdminManagementModalComponent, data: { client: pClient } },
        title: this.translate.instant('lang.adminClientEdit.manageAdmins'),
        icon: 'edit',
        modalOptions: { windowClass: 'bigXL' },
      })
      .then(() => {
        this.requestDebounced();
      })
      .catch(() => {
        this.requestDebounced();
      });
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'edit',
        tooltip: 'lang.globals.edit',
        link: RoutingID.adminClientManagementEdit,
        pathParams: [{ id: 'clientID', value: item?.id }],
      });
      item.sbButtons.push({
        icon: 'user',
        action: 'admins',
        tooltip: 'lang.globals.admins',
      });
      item.sbButtons.push({
        icon: 'list',
        tooltip: 'lang.admin.licences',
        link: RoutingID.adminClientLicences,
        pathParams: [{ id: 'clientID', value: item?.id }],
      });
    });
    return result;
  }

  resolveSplitButtonAction(client: ClientDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'admins':
        this.manageAdmins(client);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
